import { Glass } from "@coach/ui";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  markConfirmedTeam,
  switchActiveTeam,
} from "../lib/active-selection.js";
import {
  useActiveOrgName,
  useActiveTeamId,
  useMyTeams,
} from "../lib/use-active-org.js";

/**
 * `/select/team` — Team-Auswahl innerhalb des aktiven Vereins. Same shell
 * + interaction model as `/select/org`. Only shown when the active org
 * has more than one team the caller can reach. After picking we POST
 * `/api/v1/teams/active` and hard-reload so every repo + the sync engine
 * picks up the new team consistently.
 */
export function SelectTeamPage() {
  const teams = useMyTeams();
  const activeTeamId = useActiveTeamId();
  const orgName = useActiveOrgName();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function pick(teamId: string) {
    setBusyId(teamId);
    setErr(null);
    try {
      if (teamId === activeTeamId) {
        markConfirmedTeam(teamId);
        navigate("/", { replace: true });
        return;
      }
      await switchActiveTeam(teamId);
    } catch (e) {
      setErr((e as Error).message ?? "Fehler beim Wechsel");
      setBusyId(null);
    }
  }

  return (
    <div className="grid min-h-svh place-items-center bg-background p-6">
      <Glass
        level="floating"
        padding="lg"
        className="flex w-full max-w-md flex-col gap-5"
      >
        <header className="text-center">
          <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
            {orgName ?? "Coach Organizer"}
          </span>
          <h1 className="text-display-sm font-semibold">Team auswählen</h1>
          <p className="mt-2 text-body-sm text-muted-foreground">
            Mehrere Teams in diesem Verein. Wähle das Team, mit dem du arbeiten
            möchtest — Wechsel jederzeit über den Account-Menü- punkt „Team
            wechseln".
          </p>
        </header>

        {teams.length === 0 ? (
          <p className="text-center text-body-sm text-muted-foreground">
            Noch kein Team im Verein. Lege im Vereinsbereich eines an.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {teams.map((t) => {
              const isActive = t.id === activeTeamId;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => pick(t.id)}
                    className="flex w-full items-center justify-between gap-3 rounded-md border border-white/10 bg-white/2 p-3 text-left hover:border-pitch-lime/40 disabled:opacity-60"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-body-md font-medium">
                        {t.name}
                      </span>
                      <span className="text-body-sm text-muted-foreground">
                        {t.league ?? "Ohne Liga"}
                        {isActive ? " · aktuell aktiv" : ""}
                      </span>
                    </div>
                    <span className="grid h-10 w-10 place-items-center rounded-md bg-white/5 text-label-caps uppercase text-muted-foreground">
                      {t.name.slice(0, 2)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {err && (
          <p className="text-body-sm text-tactical-red" role="alert">
            {err}
          </p>
        )}
      </Glass>
    </div>
  );
}
